import React, { FC, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { Form, useFormikContext } from 'formik';

import { ResponseStatus } from 'app/api';
import { Button, ButtonIntent, ButtonType } from 'app/design-system/components/Button';
import { FormControl } from 'app/design-system/components/FormControl';
import { InputSize } from 'app/design-system/components/Input';
import { useTranslation } from 'app/lang';
import InputWithAnalytics from 'app/widgets/InputsWithAnalytics/Input';

import { SearchParam } from './constants';

import dictionary from './PromocodeForm.dictionary.json';

import styles from './PromocodeForm.module.scss';

//Исключает все символы кроме латинского алфавита, кириличиских символов и цифр
const DENIED_CHARS = /[^a-zA-Z\u0400-\u052F\u2DE0-\u2DFF\uA640-\uA69F\d]/gi;

type FormikValues = {
  code: string;
};

type ContentProps = {
  charError: ReactNode;
  setCharError: (value: string) => void;
  isRegistration: boolean;
};

const Content: FC<ContentProps> = ({ charError, setCharError, isRegistration }) => {
  const { t } = useTranslation();
  const {
    values,
    errors,
    status,
    isSubmitting,
    setErrors,
    setStatus,
    setFieldValue,
    resetForm,
  } = useFormikContext<FormikValues>();
  const inputRef = useRef<HTMLInputElement>(null);
  const isInputFocused = useLocation().search.includes(SearchParam.PROMO);
  const successStatus = status ? status['response-status'] === ResponseStatus.SUCCESS : false;

  const changeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const isDeniedChar = Boolean(value.match(DENIED_CHARS));
    if (isDeniedChar) {
      setCharError(t(dictionary.invalidChar));
    }
    else {
      setCharError('');
    }
    setFieldValue(
      name,
      value
        .replace(DENIED_CHARS, ''),
    );
  }, []);

  const focusHandler = useCallback(() => {
    if (successStatus) {
      return;
    }
    setErrors({});
    setStatus();
  }, [successStatus]);

  const clearHandler = useCallback(() => {
    resetForm();
    setErrors({});
    setStatus(0);
  }, []);

  useEffect(() => {
    if (inputRef.current && isInputFocused) {
      inputRef.current.focus();
    }
  }, [inputRef.current, isInputFocused]);

  return (
    <Form>
      <FormControl error={errors.code || charError}
                   caption={successStatus ? status.message : ''}
                   className={classNames(styles.control, {
                     [styles.clearFieldHelperSize]: errors.code || successStatus || charError,
                   })}>
        <InputWithAnalytics value={values.code}
                            ref={inputRef}
                            disabled={isSubmitting || successStatus}
                            placeholder={t(dictionary.placeholder)}
                            error={!!errors.code || !!charError}
                            name="code"
                            data-ga-event="promoCodeInput"
                            inputSize={InputSize.MEDIUM}
                            className={styles.field}
                            onChange={changeHandler}
                            onFocus={focusHandler}
                            iconRight={successStatus && status && 'check'}
                            iconRightProps={{
                              size: 16,
                              className: styles.icon,
                            }} />
        {(successStatus && !isRegistration) ? (
          <Button
            className={styles.button}
            onClick={clearHandler}
            type={ButtonType.BUTTON}
            intent={ButtonIntent.PRIMARY}>
            {t(dictionary.clearText)}
          </Button>
        ) : (
          <Button
            className={styles.button}
            type={ButtonType.SUBMIT}
            intent={ButtonIntent.CALL_TO_ACTION}
            loading={isSubmitting && !successStatus}
            disabled={successStatus && isRegistration}
            data-ga-event="promoCodeApply"
            data-ga-event-value={values?.code}>
            {t(dictionary.applyText)}
          </Button>
        )}
      </FormControl>
    </Form>
  );
};

export default Content;
